/**
 * The research card's rows, from the snapshot only (plan K0/K1: TP's look, our real data). Top-level rows are what
 * Scout's own jobs report (site read, research, ad library, ad analysis, funnel); CI's stages nest under the research
 * row that owns them. A job the snapshot does not list has not started: it has no row (nothing is shown as queued,
 * done or failed that the BFF never said). The pure derivations behind the card's findings live here too.
 */
import {
  adAnalysisPlatformOfJobKind,
  type BrandGuideResponse,
  type ResearchProgress,
  type ResearchSummary,
  type SessionSnapshot,
  type StepState,
} from '@/contracts/onboarding-session';
import { safeColor } from '@/lib/safeColor';
import { safeHttpUrl } from '@/lib/safeHttpUrl';

import { COULDNT_READ, countLabel, RESEARCH_STEPS, researchTokenLabel } from '../copy';
import { channelName } from '../quiz/questions';
import { isTerminalState } from './jobState';
import { voiceEntries } from './voiceEntries';

/** One row of the research card: a job or a CI stage and its state. `unavailable` is a failed read; nothing is shown as done that was not. */
export interface TimelineStep {
  id: string;
  label: string;
  status: StepState;
  /** A short muted note after the label ("12 products", "Couldn't read"). */
  detail?: string;
  /** Rows that belong to this one (CI's stages, under the research row), drawn indented. Top-level rows only. */
  children?: TimelineStep[];
}

/** The state of a job the snapshot lists; `undefined` when it does not (not started). Without a snapshot nothing is listed either. */
const listedState = (snapshot: SessionSnapshot | null, kind: string): StepState | undefined =>
  snapshot?.jobs.find((j) => j.kind === kind)?.state;

/**
 * One "Ad analysis" step per `adAnalysis:*` job the BFF has started, in its own state. The job only exists once
 * the ads were read, so there is no step before that (nothing is shown as queued that has not been queued).
 */
function adAnalysisSteps(snapshot: SessionSnapshot | null): TimelineStep[] {
  const jobs = (snapshot?.jobs ?? []).flatMap((job) => {
    const platform = adAnalysisPlatformOfJobKind(job.kind);
    return platform ? [{ platform, state: job.state }] : [];
  });
  return jobs.map(({ platform, state }) => ({
    id: `adAnalysis:${platform}`,
    label: jobs.length > 1 ? `${channelName(platform)} ad analysis` : RESEARCH_STEPS.adAnalysis,
    status: state,
    detail: state === 'unavailable' ? COULDNT_READ : undefined,
  }));
}

/** The research row's muted note: while it runs, the stage CI is on and the job count ("Competitors · 7 of 12 jobs"), else the count. */
function researchDetail(snapshot: SessionSnapshot | null, state: StepState): string | undefined {
  const progress = snapshot?.researchProgress;
  if (progress && progress.totalJobs > 0) {
    const running = state === 'running' ? progress.stages?.find((s) => s.state === 'running') : undefined;
    return running
      ? `${researchTokenLabel(running.name)} · ${RESEARCH_STEPS.jobsProgress(progress.completedJobs, progress.totalJobs)}`
      : RESEARCH_STEPS.jobsProgress(progress.completedJobs, progress.totalJobs);
  }
  return state === 'unavailable' ? COULDNT_READ : undefined;
}

/**
 * Scout's per-job rows, in the order the flow starts them. The site read and the research are the two every
 * session starts, so they show (queued) before the first snapshot; the ad library and the funnel only once the
 * snapshot lists them.
 */
export function timelineSteps(snapshot: SessionSnapshot | null, guide: BrandGuideResponse | null = null): TimelineStep[] {
  const summary = snapshot?.research;
  const productCount = summary ? researchProductCount(summary, guide) : null;
  const rows: TimelineStep[] = [];

  const site = snapshot ? listedState(snapshot, 'siteProfile') : 'queued';
  if (site) {
    rows.push({
      id: 'siteProfile',
      label: RESEARCH_STEPS.readSite,
      status: site,
      detail:
        productCount !== null
          ? countLabel(productCount, 'product', 'products')
          : site === 'unavailable'
            ? COULDNT_READ
            : undefined,
    });
  }

  const research = snapshot ? listedState(snapshot, 'research') : 'queued';
  if (research) {
    rows.push({ id: 'research', label: RESEARCH_STEPS.marketResearch, status: research, detail: researchDetail(snapshot, research) });
  }

  const ads = listedState(snapshot, 'adDiscovery:meta');
  if (ads) {
    rows.push({
      id: 'adDiscovery:meta',
      label: RESEARCH_STEPS.adLibrary,
      status: ads,
      detail:
        summary?.liveAdCount != null
          ? countLabel(summary.liveAdCount, 'live ad', 'live ads')
          : ads === 'unavailable'
            ? COULDNT_READ
            : undefined,
    });
  }

  rows.push(...adAnalysisSteps(snapshot));

  const funnel = listedState(snapshot, 'funnel');
  if (funnel) {
    rows.push({ id: 'funnel', label: RESEARCH_STEPS.funnel, status: funnel, detail: funnel === 'unavailable' ? COULDNT_READ : undefined });
  }
  return rows;
}

/**
 * CI's own stages as steps, each with the jobs that belong to it (`job.stage`) as its note. A job's state is its
 * stage's (CI has no per-job status), so the jobs ride on the stage row; a job whose stage this run does not list
 * gets its own row with its own state. Empty when the BFF sent neither list.
 */
export function stageSteps(progress: ResearchProgress | undefined): TimelineStep[] {
  const stages = progress?.stages ?? [];
  const jobs = progress?.jobs ?? [];
  const stageNames = new Set(stages.map((s) => s.name));
  const rows: TimelineStep[] = stages.map((stage, i) => {
    const own = jobs.filter((j) => j.stage === stage.name).map((j) => researchTokenLabel(j.name));
    return {
      id: `stage:${i}:${stage.name}`,
      label: researchTokenLabel(stage.name),
      status: stage.state,
      detail: own.length > 0 ? own.join(', ') : undefined,
    };
  });
  jobs.forEach((job, i) => {
    if (stageNames.has(job.stage)) return;
    rows.push({ id: `job:${i}:${job.name}`, label: researchTokenLabel(job.name), status: job.state });
  });
  return rows;
}

/**
 * The card's top-level rows, with CI's stages nested as the `children` of the research row they belong to. Order is
 * what the flow starts and the BFF reports; nothing is reordered by state.
 */
export function researchSteps(snapshot: SessionSnapshot | null, guide: BrandGuideResponse | null = null): TimelineStep[] {
  const stages = stageSteps(snapshot?.researchProgress);
  return timelineSteps(snapshot, guide).map((step) => (step.id === 'research' && stages.length > 0 ? { ...step, children: stages } : step));
}

/** How many of the top-level rows ended (done, partly done, failed or skipped): the header's "N of M steps". */
export const terminalStepCount = (steps: readonly TimelineStep[]): number => steps.filter((s) => isTerminalState(s.status)).length;

/** "3 of 4 steps" over the top-level rows: the card's header and the Tasks panel both read it here, so they always agree. Null when there are no rows. */
export function researchStepsProgress(snapshot: SessionSnapshot | null): string | null {
  const steps = researchSteps(snapshot);
  return steps.length > 0 ? RESEARCH_STEPS.stepsProgress(terminalStepCount(steps), steps.length) : null;
}

/**
 * The rows the card shows while collapsed: the last `tail`, plus any running row that sits earlier (so work in
 * progress is never behind "N earlier steps"), in the original order. `hidden` counts only the rows left out.
 */
export function visibleSteps(steps: readonly TimelineStep[], tail: number): { shown: TimelineStep[]; hidden: number } {
  const windowStart = Math.max(0, steps.length - tail);
  const shown = steps.filter((s, i) => i >= windowStart || s.status === 'running');
  return { shown, hidden: steps.length - shown.length };
}

/* --------------------------------- findings -------------------------------- */

/**
 * The prospect's logo URL, from the BFF's own read of their site: the logo it declares (`logoUrl`), else the site's icon
 * (`iconUrl`), else null (the card then draws the brand's initial). The site image (`og:image`) is a banner, never a
 * logo, so it is not a candidate. An address that is not http(s) is skipped, not drawn.
 */
export function prospectLogoUrl(guide: BrandGuideResponse | null): string | null {
  if (!guide) return null;
  return safeHttpUrl(guide.logoUrl.value) ?? safeHttpUrl(guide.iconUrl.value) ?? null;
}

/** The brand profile as far as it is read: a part the guide has not got is left out, never filled in. */
export function profileOf(guide: BrandGuideResponse | null) {
  if (!guide) return null;
  const font = guide.fonts.value?.[0]?.trim() || null;
  const colors = (guide.colors.value ?? []).flatMap((c) => {
    const css = safeColor(c);
    return css ? [css] : [];
  });
  const hasVoice = voiceEntries(guide.voice?.value).length > 0 || (guide.tone?.value ?? []).length > 0;
  if (!font && colors.length === 0 && !hasVoice && !prospectLogoUrl(guide)) return null;
  const quote = guide.description.value?.trim() || guide.tagline?.value?.trim() || null;
  return { font, colors, hasVoice, quote };
}

/** One best seller the research card shows: a picture and a name, and the page it lives on when known. */
export interface BestSeller {
  name: string;
  imageUrl: string;
  url?: string;
}

const normalizedName = (name: string) => name.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * A product page's identity for de-duplication: the host is case-insensitive, the path and query are not (two pages
 * that differ only in path case are two pages). A trailing slash and a `#fragment` are ignored. An address `URL` cannot
 * parse (a relative path, say) falls back to its trimmed text without the trailing slash.
 */
const normalizedUrl = (url: string | null | undefined): string | null => {
  const trimmed = url?.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    const path = parsed.pathname.replace(/\/+$/, '');
    return `${parsed.protocol}//${parsed.host.toLowerCase()}${path}${parsed.search}`;
  } catch {
    return trimmed.replace(/\/+$/, '');
  }
};

/**
 * The best sellers that came with a picture (a name alone is in the guide card below): CI's `topSellers` first
 * (skipping the ones CI found no image for), then the site's own list, each product once (same URL or same name).
 * Reading both is what lets a non-Shopify store, whose own list is empty, still show the row.
 */
export function bestSellersOf(guide: BrandGuideResponse | null): BestSeller[] {
  const candidates: BestSeller[] = [
    ...(guide?.topSellers?.value ?? []).flatMap((s) => (s.imageUrl?.trim() ? [{ name: s.name, imageUrl: s.imageUrl, url: s.url ?? undefined }] : [])),
    ...(guide?.products?.value ?? []).filter((p) => p.imageUrl.trim() !== ''),
  ];
  const seenUrls = new Set<string>();
  const seenNames = new Set<string>();
  const sellers: BestSeller[] = [];
  for (const candidate of candidates) {
    const url = normalizedUrl(candidate.url);
    const name = normalizedName(candidate.name);
    if ((url !== null && seenUrls.has(url)) || (name !== '' && seenNames.has(name))) continue;
    if (url !== null) seenUrls.add(url);
    if (name !== '') seenNames.add(name);
    sellers.push(candidate);
  }
  return sellers;
}

/**
 * The product count the research card, its steps and the Tasks panel all show. The site read counts Shopify products
 * only, and the BFF sends `0` (not null) when a non-Shopify site was read with none, while CI's top sellers may still
 * be there. So a positive count wins; a zero or missing one falls back to the best sellers that came with a picture,
 * and only when there are none is the site's own `0` kept. Null when neither source has a number.
 */
export function researchProductCount(summary: Pick<ResearchSummary, 'productCount'> | null | undefined, guide: BrandGuideResponse | null): number | null {
  const n = summary?.productCount ?? null;
  if (n !== null && n > 0) return n;
  return bestSellersOf(guide).length || n;
}
